/* eslint-disable complexity */
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  LocalFilterOptions,
  ResetFilterOptions,
} from 'src/components/organisms';
import { TimeframeInput } from 'src/graphql';
import { FormState, GlobalFilterLocation } from 'src/types';
import { isMatchingArray } from 'src/utils';
import { diff } from 'src/utils/time';

interface Parameters {
  filters: LocalFilterOptions;
  resetFilters?: ResetFilterOptions;
  isOpen: boolean;
  selectedLocationFilter?: GlobalFilterLocation;
  originalSelectedLocationFilter: GlobalFilterLocation;
  setSelectedLocationFilter?: (location: GlobalFilterLocation) => void;
}

export const useLocalFilters = ({
  filters,
  resetFilters,
  isOpen,
  selectedLocationFilter,
  originalSelectedLocationFilter = GlobalFilterLocation.SITES,
  setSelectedLocationFilter,
}: Parameters) => {
  const [selectedSites, setSelectedSites] = useState<string[] | undefined>(
    filters.site?.selectedOptions,
  );
  const [selectedCountries, setSelectedCountries] = useState<
    string[] | undefined
  >(filters.country?.selectedOptions);
  const [selectedDepartments, setSelectedDepartments] = useState<
    string[] | undefined
  >(filters.department?.selectedOptions);
  const [selectedCategories, setSelectedCategories] = useState<
    string[] | undefined
  >(filters.category?.selectedOptions);
  const [selectedTimeframe, setSelectedTimeframe] = useState<
    TimeframeInput | undefined
  >(filters.time?.timeframe);
  const [timeframeState, setTimeframeState] = useState<FormState>(
    FormState.DEFAULT,
  );
  const [applyButtonDisabled, setApplyButtonDisabled] = useState(true);

  const selectFilterOnChange = (
    optionsList: string[],
    setOptionsList: Dispatch<SetStateAction<string[] | undefined>>,
    limit?: number,
  ) => {
    return (option: string) => {
      if (optionsList.includes(option)) {
        setOptionsList(optionsList.filter((o) => o !== option));
      } else {
        if (!limit || (limit && optionsList.length < limit)) {
          setOptionsList([...optionsList, option]);
        }
      }
    };
  };

  const onSelectAll = (
    filterOption: keyof LocalFilterOptions,
    selectedOptionsList: string[],
    setSelectedOptionsList: Dispatch<SetStateAction<string[] | undefined>>,
  ) => {
    if (
      filters[filterOption] &&
      filterOption !== 'time' &&
      filterOption !== 'selectedLocationFilter'
    ) {
      return () => {
        if (filters[filterOption]?.limit) {
          if (selectedOptionsList.length === filters[filterOption]?.limit) {
            setSelectedOptionsList([]);
          } else {
            if (filters[filterOption]?.selectAllOptions) {
              setSelectedOptionsList(filters[filterOption]?.selectAllOptions);
            } else {
              setSelectedOptionsList(
                filters[filterOption]?.options.slice(
                  0,
                  filters[filterOption]?.limit,
                ),
              );
            }
          }
        } else {
          if (
            selectedOptionsList.length === filters[filterOption]?.options.length
          ) {
            setSelectedOptionsList([]);
          } else {
            if (filters[filterOption]?.selectAllOptions) {
              setSelectedOptionsList(filters[filterOption]?.selectAllOptions);
            } else {
              setSelectedOptionsList(filters[filterOption]?.options);
            }
          }
        }
      };
    }
  };

  const timeFilterOnChange = (time: TimeframeInput) => {
    if (time?.start && time?.end && diff(time.start, time.end) > 0) {
      setTimeframeState(FormState.ERROR);
    } else {
      setTimeframeState(FormState.DEFAULT);
    }
    setSelectedTimeframe(time);
  };

  const clearFilters = () => {
    if (filters.site && resetFilters?.siteOptions) {
      setSelectedSites(resetFilters.siteOptions);
    }
    if (filters.country && resetFilters?.countryOptions) {
      setSelectedCountries(resetFilters.countryOptions);
    }
    if (filters.time && resetFilters?.timeframe) {
      setSelectedTimeframe(resetFilters.timeframe);
      setTimeframeState(FormState.DEFAULT);
    }
    if (filters.department && resetFilters?.departmentOptions) {
      setSelectedDepartments(resetFilters.departmentOptions);
    }
    if (filters.category && resetFilters?.categoryOptions) {
      setSelectedCategories(resetFilters.categoryOptions);
    }
    if (setSelectedLocationFilter && resetFilters?.globalFilterLocation) {
      setSelectedLocationFilter(resetFilters?.globalFilterLocation);
    }
  };

  const selectedSitesChanged = !isMatchingArray(
    selectedSites ?? [],
    filters.site?.selectedOptions ?? [],
  );

  const selectedCountriesChanged = !isMatchingArray(
    selectedCountries ?? [],
    filters.country?.selectedOptions ?? [],
  );

  const newLocationFilterSelected =
    selectedLocationFilter !== originalSelectedLocationFilter;

  const selectedLocationsChanged =
    selectedSitesChanged ||
    selectedCountriesChanged ||
    newLocationFilterSelected;

  const selectedDepartmentsChanged = !isMatchingArray(
    selectedDepartments ?? [],
    filters.department?.selectedOptions ?? [],
  );

  const selectedCategoriesChanged = !isMatchingArray(
    selectedCategories ?? [],
    filters.category?.selectedOptions ?? [],
  );

  const selectedTimeframeChanged =
    new Date(selectedTimeframe?.start).getTime() !==
      new Date(filters.time?.timeframe.start).getTime() ||
    new Date(selectedTimeframe?.end).getTime() !==
      new Date(filters.time?.timeframe.end).getTime();

  useEffect(() => {
    setSelectedSites(filters.site?.selectedOptions);
    setSelectedCountries(filters.country?.selectedOptions);
    setSelectedTimeframe(filters.time?.timeframe);
    setSelectedDepartments(filters.department?.selectedOptions);
    setSelectedCategories(filters.category?.selectedOptions);
    setTimeframeState(FormState.DEFAULT);
  }, [
    filters.category?.selectedOptions,
    filters.department?.selectedOptions,
    filters.site?.selectedOptions,
    filters.country?.selectedOptions,
    filters.time?.timeframe,
    isOpen,
  ]);

  useEffect(() => {
    const selectedFiltersEmpty =
      (selectedCategories && !selectedCategories.length) ||
      (selectedDepartments && !selectedDepartments.length) ||
      (selectedSites && !selectedSites.length) ||
      (selectedCountries && !selectedCountries.length);
    const selectedFiltersHaveChanged =
      selectedLocationsChanged ||
      selectedDepartmentsChanged ||
      selectedCategoriesChanged ||
      selectedTimeframeChanged;

    if (selectedFiltersEmpty) {
      setApplyButtonDisabled(true);
    } else if (
      selectedFiltersHaveChanged &&
      timeframeState !== FormState.ERROR
    ) {
      setApplyButtonDisabled(false);
    } else {
      setApplyButtonDisabled(true);
    }
  }, [
    selectedSites,
    selectedCountries,
    selectedDepartments,
    selectedCategories,
    selectedTimeframe,
    selectedCategoriesChanged,
    selectedLocationsChanged,
    selectedDepartmentsChanged,
    selectedTimeframeChanged,
    timeframeState,
  ]);

  const locationFilter =
    selectedLocationFilter === GlobalFilterLocation.SITES
      ? filters.site
      : filters.country;
  const selectedLocationFilterState =
    selectedLocationFilter === GlobalFilterLocation.SITES
      ? selectedSites
      : selectedCountries;
  const setSelectedLocationFilterOptions =
    selectedLocationFilter === GlobalFilterLocation.SITES
      ? setSelectedSites
      : setSelectedCountries;
  const selectedLocationLimit =
    selectedLocationFilter === GlobalFilterLocation.SITES
      ? filters.site?.limit
      : filters.country?.limit;
  const selectedLocationOptions =
    selectedLocationFilter === GlobalFilterLocation.SITES
      ? filters.site?.options
      : filters.country?.options;
  const locationFilterOption: keyof LocalFilterOptions =
    selectedLocationFilter === GlobalFilterLocation.SITES ? 'site' : 'country';

  return {
    selectedSites,
    selectedCountries,
    selectedDepartments,
    setSelectedDepartments,
    selectedCategories,
    setSelectedCategories,
    selectedTimeframe,
    timeframeState,
    applyButtonDisabled,
    selectFilterOnChange,
    timeFilterOnChange,
    clearFilters,
    onSelectAll,
    selectedDepartmentsChanged,
    selectedCategoriesChanged,
    selectedLocationsChanged,
    selectedTimeframeChanged,
    locationFilter,
    selectedLocationFilterState,
    setSelectedLocationFilterOptions,
    selectedLocationLimit,
    selectedLocationOptions,
    locationFilterOption,
  };
};
